import JSZip from 'jszip'
import { saveAs } from 'file-saver'

// Códigos de la app → códigos en la plantilla oficial
const CODE_MAP = {
  'CNT 1': 'CONT 1',
  'CNT 2': 'CONT 2',
  'CNT 3': 'CONT 3',
  'CNT 4': 'CONT 4',
  'CNT 5': 'CONT 5',
  'SyB 8': 'SYB 8',
  // INO 1 se rellena bajo el heading «Innovaciones Aprobadas» de la plantilla
  // (única sección de la Innovación con Ttulo20). Caso especial en el loop principal.
  'INO 1': 'Innovaciones Aprobadas',
}

function esc(str) {
  if (!str) return ''
  return String(str)
    // Quita marcado de la IA (backticks e ítálicas markdown) que Word no renderiza
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

// Prefijo compacto del código de criterio para etiquetar evidencias
// "GST 1" → "GST1"  ·  "SyB 10" → "SyB10"  ·  "AG 1" → "AG1"
function getCriterioPrefix(criteriaCode) {
  if (!criteriaCode) return ''
  return String(criteriaCode).replace(/\s+/g, '')
}

// Colapsa evidencias fragmentadas (nombre_parteXdeN.pdf) a una sola entrada por documento maestro
function collapseEvidences(evidences) {
  const fragmentRe = /_parte\d+de\d+/i
  const seen = new Map()
  for (const ev of evidences) {
    const baseName = String(ev.file_name || '').replace(fragmentRe, '')
    if (!seen.has(baseName)) {
      seen.set(baseName, { ...ev, file_name: baseName })
    }
  }
  return Array.from(seen.values())
}

// opts: { bold, justify, italic, spaceAfter, spaceBefore }
function makePara(text, bold = false, opts = {}) {
  const t = esc(text)
  const justify = opts.justify !== false // por defecto justificado
  const italic = opts.italic || false
  const spaceAfter = opts.spaceAfter !== undefined ? opts.spaceAfter : 80
  const spaceBefore = opts.spaceBefore !== undefined ? opts.spaceBefore : 0

  const jc = justify ? '<w:jc w:val="both"/>' : ''
  const spacing = `<w:spacing w:before="${spaceBefore}" w:after="${spaceAfter}"/>`
  const rPrBold = bold ? '<w:b/>' : ''
  const rPrItalic = italic ? '<w:i/>' : ''

  return `<w:p>
    <w:pPr>${jc}${spacing}<w:rPr>${rPrBold}${rPrItalic}</w:rPr></w:pPr>
    <w:r><w:rPr>${rPrBold}${rPrItalic}</w:rPr><w:t xml:space="preserve">${t}</w:t></w:r>
  </w:p>`
}

function makeEmptyPara() {
  return `<w:p><w:pPr><w:spacing w:before="0" w:after="0"/></w:pPr></w:p>`
}

// ============================================================
// FUNCIÓN PRINCIPAL
// ============================================================
export async function fillBREAMTemplate(project, BREEAM_CRITERIA) {
  try {
    // 1. Cargar plantilla desde public/
    const response = await fetch('/Informe-de-Evaluacion-Nueva-Construccion_Ed01.docx')
    if (!response.ok) {
      throw new Error('Plantilla no encontrada. Verifica que el archivo está en la carpeta public/')
    }
    const arrayBuffer = await response.arrayBuffer()

    // 2. Abrir el docx como ZIP
    const zip = await JSZip.loadAsync(arrayBuffer)

    // 3. Obtener el XML del documento
    let xml = await zip.file('word/document.xml').async('string')

    // 4a. Eliminar ejemplos en rojo que empiezan con "P" (p.ej. de documentación)
    xml = removeRedPExamples(xml)

    // 4b. Rellenar placeholders de portada + datos del edificio con PROJECT-DETAILS
    xml = fillProjectFields(xml, project)

    // 4. Encontrar todas las secciones de criterios en el cuerpo (no en el índice)
    const BODY_START = 300000 // posición aproximada donde termina el índice
    const headings = findAllHeadings(xml, BODY_START)

    if (headings.length === 0) {
      throw new Error('No se encontraron criterios en la plantilla. El archivo puede estar dañado.')
    }

    // 5. Preparar evaluaciones con su posición en la plantilla
    const evaluations = Object.values(project.evaluations || {})

    const evsWithPos = evaluations
      .map(ev => {
        const templateCode = CODE_MAP[ev.criteria_code] || ev.criteria_code
        // Buscar el encabezado que empiece con el código del criterio
        const heading = headings.find(h =>
          h.text.startsWith(templateCode + ' -') ||
          h.text.startsWith(templateCode + '\u00a0-') ||  // non-breaking space
          h.text.startsWith(templateCode + ' –') ||       // em dash
          h.text === templateCode                         // INO 1 → «Innovaciones Aprobadas»
        )
        return { ev, heading, templateCode }
      })
      .filter(x => x.heading)

    if (evsWithPos.length === 0) {
      throw new Error('Ninguna evaluación encontró su sección en la plantilla.')
    }

    // 6. Procesar en ORDEN INVERSO para evitar desplazamiento de posiciones
    evsWithPos.sort((a, b) => b.heading.pos - a.heading.pos)

    for (const { ev, heading, templateCode } of evsWithPos) {
      const headingIdx = headings.findIndex(h => h.pos === heading.pos)
      const nextHeading = headings[headingIdx + 1]
      const sectionStart = heading.pos
      const sectionEnd = nextHeading ? nextHeading.pos : xml.length

      let section = xml.slice(sectionStart, sectionEnd)

      // Caso especial INO 1: la sección «Innovaciones Aprobadas» de la plantilla
      // no tiene subtítulos Objetivo/Documentación/Justificación estándar. Insertamos
      // el bloque INO 1 (puntuación + resumen + subcriterios) directamente tras el heading.
      if (templateCode === 'Innovaciones Aprobadas') {
        section = fillInnovacionesSection(section, ev, BREEAM_CRITERIA)
        xml = xml.slice(0, sectionStart) + section + xml.slice(sectionEnd)
        continue
      }

      // a) Reemplazar [No.] con los puntos finales (auditoría si existe, si no técnicos)
      const finalPts = ev.puntos_auditoria ?? ev.points_obtained ?? 0
      section = section.replace('[No.]', String(finalPts))

      // b) Rellenar tabla de documentación con evidencias (si existen)
      section = fillDocumentation(section, ev)

      // c) Reemplazar texto de justificación placeholder con la justificación real
      section = fillJustification(section, ev, BREEAM_CRITERIA)

      xml = xml.slice(0, sectionStart) + section + xml.slice(sectionEnd)
    }

    // 7. Guardar el XML modificado de vuelta en el ZIP
    zip.file('word/document.xml', xml)

    // 8. Generar blob y descargar
    const blob = await zip.generateAsync({
      type: 'blob',
      mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    })

    const safeName = project.name.replace(/[^a-zA-Z0-9áéíóúñÁÉÍÓÚÑ\s_-]/g, '').replace(/\s+/g, '_')
    saveAs(blob, `Informe_BREEAM_${safeName}_Completado.docx`)

  } catch (err) {
    console.error('Error en fillBREAMTemplate:', err)
    alert('❌ Error al generar la plantilla oficial:\n\n' + err.message)
  }
}

// ============================================================
// HELPERS — Búsqueda de secciones
// ============================================================

function removeRedPExamples(xml) {
  // Prefijos de párrafos rojos que son NOTAS DE INSTRUCCIÓN del asesor (borrar del docx final)
  const INSTRUCTION_PREFIXES = [
    'P',                                    // "P.ej." · "P. ej."
    'Asegúrese',                            // "Asegúrese de proporcionar..."
    'Nota:',                                // Nota introductoria
    'La Justificación debe',                // "La Justificación debe proporcionar..."
    'Cuando se aporten pruebas',            // Nota sobre no conformidad
    'Además, si un Punto BREEAM',           // Nota sobre puntos no obtenidos
    '(Los proyectos sensibles',             // Nota sobre seguridad
    '(Borre el texto',                      // "(Borre el texto que se muestra en la sección siguiente…)"
    'Borre el texto',                       // Variante sin paréntesis
  ]
  const result = []
  let idx = 0
  while (idx < xml.length) {
    const pStart = xml.indexOf('<w:p ', idx)
    if (pStart === -1) { result.push(xml.slice(idx)); break }
    result.push(xml.slice(idx, pStart))
    const pEnd = xml.indexOf('</w:p>', pStart) + 6
    const para = xml.slice(pStart, pEnd)
    const hasRed = para.includes('FF0000') || para.includes('C00000') || para.includes('ff0000')
    const text = para.replace(/<[^>]+>/g, '').trim()
    const isInstruction = hasRed && INSTRUCTION_PREFIXES.some(pre => text.startsWith(pre))
    if (isInstruction) {
      // Omitir el párrafo
    } else {
      result.push(para)
    }
    idx = pEnd
  }
  return result.join('')
}

function findAllHeadings(xml, bodyStart) {
  const headings = []
  let idx = bodyStart

  while (idx < xml.length) {
    const tIdx = xml.indexOf('Ttulo20', idx)
    if (tIdx === -1) break

    const pStart = xml.lastIndexOf('<w:p ', tIdx)
    const pEnd = xml.indexOf('</w:p>', tIdx) + 6

    if (pStart < 0 || pEnd < 6) { idx = tIdx + 1; continue }

    const text = xml.slice(pStart, pEnd)
      .replace(/<[^>]+>/g, '')
      .replace(/\s+/g, ' ')
      .trim()

    // Solo párrafos que sean encabezados de criterio (tienen " - " o " – " en el texto)
    if (text.includes(' - ') || text.includes('\u00a0-') || text.includes(' – ')) {
      headings.push({ pos: pStart, text })
    }
    // Excepción para el heading «Innovaciones Aprobadas» (INO 1) que no tiene guión.
    else if (text === 'Innovaciones Aprobadas') {
      headings.push({ pos: pStart, text })
    }

    idx = pEnd
  }

  return headings
}

// ============================================================
// HELPERS — Documentación
// ============================================================

function fillDocumentation(section, ev) {
  const collapsed = collapseEvidences(ev.evidences || [])
  const evidences = collapsed.sort((a, b) => a.file_name.localeCompare(b.file_name, 'es', { numeric: true }))
  const prefix = getCriterioPrefix(ev.criteria_code)

  // Detectar si es un criterio marcado manualmente como No Cumple
  let isNoCumple = false
  try {
    const parsed = typeof ev.justificacion === 'string' ? JSON.parse(ev.justificacion) : ev.justificacion
    isNoCumple = parsed?.no_cumple === true
  } catch (_) {}

  if (!evidences.length && !isNoCumple) return section

  const placeholderIdx = section.indexOf('Enumere a')
  if (placeholderIdx === -1) return section

  // Encontrar el párrafo contenedor
  const pStart = section.lastIndexOf('<w:p ', placeholderIdx)
  const pEnd = section.indexOf('</w:p>', placeholderIdx) + 6

  const evidenceLine = (e, i) => `${prefix}.${i + 1}: ${e.file_name}`

  let evidenceXml
  if (isNoCumple && !evidences.length) {
    evidenceXml = makePara('NO CUMPLE', true)
  } else if (isNoCumple) {
    evidenceXml = makePara('NO CUMPLE', true) + evidences.map((e, i) => makePara(evidenceLine(e, i))).join('')
  } else {
    evidenceXml = evidences.map((e, i) => makePara(evidenceLine(e, i))).join('')
  }

  return section.slice(0, pStart) + evidenceXml + section.slice(pEnd)
}

// ============================================================
// HELPERS — Justificación
// ============================================================

function fillJustification(section, ev, BREEAM_CRITERIA) {
  // Encontrar el subtítulo "Justificación" (estilo ttulo10)
  let subtitleEnd = -1
  let idx = 0

  while (idx < section.length) {
    const tIdx = section.indexOf('ttulo10', idx)
    if (tIdx === -1) break

    const pStart = section.lastIndexOf('<w:p ', tIdx)
    const pEnd = section.indexOf('</w:p>', tIdx) + 6

    const text = section.slice(pStart, pEnd).replace(/<[^>]+>/g, '').trim()

    if (text.startsWith('Justificaci')) {
      subtitleEnd = pEnd
      break
    }

    idx = pEnd
  }

  if (subtitleEnd === -1) return section

  // Encontrar el párrafo con <w:sectPr> (página de cierre de sección) y extraer su <w:pPr>
  const sectPrIdx = section.indexOf('<w:sectPr', subtitleEnd)
  let sectPrPPr = ''
  let lastPlaceholderEnd = section.length

  if (sectPrIdx !== -1) {
    const pStart = section.lastIndexOf('<w:p ', sectPrIdx)
    const pEnd = section.indexOf('</w:p>', sectPrIdx) + 6

    // Extraer <w:pPr>...</w:pPr> que contiene el <w:sectPr>
    const pprOpen = section.indexOf('<w:pPr>', pStart)
    const pprClose = section.indexOf('</w:pPr>', pprOpen) + '</w:pPr>'.length

    if (pprOpen !== -1 && pprClose > pprOpen) {
      sectPrPPr = section.slice(pprOpen, pprClose)
    }

    lastPlaceholderEnd = pEnd
  }

  // Construir el XML de justificación
  const justifXml = buildJustificationXml(ev, BREEAM_CRITERIA)

  // Párrafo de cierre vacío con el sectPr preservado (necesario para mantener el layout)
  const closingPara = sectPrPPr ? `<w:p>${sectPrPPr}</w:p>` : ''

  return (
    section.slice(0, subtitleEnd) +
    justifXml +
    closingPara +
    section.slice(lastPlaceholderEnd)
  )
}

function buildJustificationXml(ev, BREEAM_CRITERIA) {
  const category = ev.category
  const code = ev.criteria_code
  const criteriaData = BREEAM_CRITERIA[category]?.criteria?.[code]

  // Parsear la justificación de la IA
  let resultado = null
  try {
    resultado = typeof ev.justificacion === 'string'
      ? JSON.parse(ev.justificacion)
      : ev.justificacion
  } catch (_) {}

  // Compatibilidad con formato legacy (array de batches)
  if (Array.isArray(resultado)) {
    const merged = {
      subcriteria: [],
      puntos_obtenidos: 0,
      resumen_ejecutivo: '',
      evidencias_verificadas: [],
      evidencias_faltantes: [],
      tabla_comparativa: [],
    }
    const subMap = {}
    for (const batch of resultado) {
      if (batch.puntos_obtenidos !== undefined) merged.puntos_obtenidos = batch.puntos_obtenidos
      if (batch.resumen_ejecutivo) merged.resumen_ejecutivo = batch.resumen_ejecutivo
      if (batch.tabla_comparativa?.length) merged.tabla_comparativa = batch.tabla_comparativa
      if (batch.evidencias_verificadas?.length) merged.evidencias_verificadas = batch.evidencias_verificadas
      if (batch.evidencias_faltantes?.length) merged.evidencias_faltantes = batch.evidencias_faltantes
      for (const sub of (batch.subcriteria || [])) {
        subMap[sub.numero] = sub
      }
    }
    merged.subcriteria = Object.values(subMap).sort((a, b) => a.numero - b.numero)
    resultado = merged
  }

  const parts = []

  const ptsMap = {
    0: 'Cero puntos', 1: 'Un punto', 2: 'Dos puntos', 3: 'Tres puntos',
    4: 'Cuatro puntos', 5: 'Cinco puntos', 6: 'Seis puntos',
  }
  const techPts = ev.points_obtained ?? 0
  const hasAudit = ev.puntos_auditoria != null
  const finalPts = hasAudit ? ev.puntos_auditoria : techPts
  const labelFor = (n) => ptsMap[n] || `${n} puntos`
  const ptsLabel = labelFor(finalPts)

  // Bloque XML para "Auditoría final del certificador" (dorado + motivo destacado)
  const makeAuditBlock = () => {
    const fechaText = ev.fecha_auditoria ? ` (${new Date(ev.fecha_auditoria).toLocaleDateString('es-ES')})` : ''
    const paras = []
    paras.push(
      `<w:p><w:pPr><w:spacing w:before="80" w:after="60"/></w:pPr>` +
      `<w:r><w:t xml:space="preserve">Puntuación técnica del asesor: </w:t></w:r>` +
      `<w:r><w:rPr><w:b/></w:rPr><w:t xml:space="preserve">${esc(labelFor(techPts))}</w:t></w:r></w:p>`
    )
    paras.push(
      `<w:p><w:pPr><w:spacing w:before="0" w:after="${ev.motivo_auditoria ? 60 : 160}"/></w:pPr>` +
      `<w:r><w:rPr><w:b/><w:color w:val="C98A2E"/></w:rPr><w:t xml:space="preserve">Auditoría final del certificador${esc(fechaText)}: </w:t></w:r>` +
      `<w:r><w:rPr><w:b/><w:color w:val="C98A2E"/></w:rPr><w:t xml:space="preserve">${esc(labelFor(finalPts))}</w:t></w:r></w:p>`
    )
    if (ev.motivo_auditoria) {
      paras.push(
        `<w:p><w:pPr><w:shd w:val="clear" w:color="auto" w:fill="FFF8E6"/><w:spacing w:before="0" w:after="200"/></w:pPr>` +
        `<w:r><w:rPr><w:b/><w:color w:val="7A4A10"/></w:rPr><w:t xml:space="preserve">Motivo: </w:t></w:r>` +
        `<w:r><w:rPr><w:i/><w:color w:val="5C4A3A"/></w:rPr><w:t xml:space="preserve">${esc(ev.motivo_auditoria)}</w:t></w:r></w:p>`
      )
    }
    return paras.join('\n')
  }

  if (resultado?.no_cumple) {
    parts.push(makePara('NO CUMPLE', true, { justify: false, spaceAfter: 120 }))
    parts.push(makePara(`Puntuación obtenida: ${ptsLabel}`, true, { spaceAfter: hasAudit ? 80 : 160 }))
    if (hasAudit) parts.push(makeAuditBlock())
    if (resultado.resumen_ejecutivo) {
      parts.push(makePara(resultado.resumen_ejecutivo, false, { spaceAfter: 80 }))
    }

  } else if (resultado?.no_aplica) {
    parts.push(makePara('NO APLICA', true, { justify: false, spaceAfter: 120 }))
    if (resultado.resumen_ejecutivo) {
      resultado.resumen_ejecutivo.split('\n').filter(l => l.trim()).forEach(line => {
        parts.push(makePara(line.trim(), false, { spaceAfter: 80 }))
      })
    } else {
      parts.push(makePara('Este criterio no es de aplicación en el presente proyecto.', false, { spaceAfter: 80 }))
    }

  } else if (resultado?.subcriteria?.length > 0) {
    // Puntuación obtenida
    parts.push(makePara(`Puntuación obtenida: ${ptsLabel}`, true, { spaceAfter: hasAudit ? 80 : 200, justify: false }))
    if (hasAudit) parts.push(makeAuditBlock())

    // Resumen ejecutivo (arriba, antes de los subcriterios) — estilo ABAMA
    if (resultado.resumen_ejecutivo) {
      parts.push(makePara('Resumen:', true, { justify: false, spaceBefore: 80, spaceAfter: 80 }))
      resultado.resumen_ejecutivo.split('\n').filter(l => l.trim()).forEach(line => {
        parts.push(makePara(line.trim(), false, { spaceAfter: 80 }))
      })
      parts.push(makeEmptyPara())
    }

    let lastFase = null
    for (let i = 0; i < resultado.subcriteria.length; i++) {
      const sub = resultado.subcriteria[i]
      const estado = sub.estado || (sub.cumple ? 'SÍ' : 'NO')
      const texCriterio = sub.enunciado || criteriaData?.subcriteria?.[sub.numero - 1] || `Criterio ${sub.numero}`
      const justif = sub.justificacion || ''

      // Separador de fase en mayúsculas cuando cambia (estilo ABAMA)
      const printedFase = sub.fase && sub.fase !== lastFase
      if (printedFase) {
        if (lastFase !== null) parts.push(makeEmptyPara())
        parts.push(makePara(sub.fase, true, { justify: false, spaceBefore: 200, spaceAfter: 120 }))
        lastFase = sub.fase
      }

      // Separador entre criterios (salvo el primero absoluto y justo tras un separador de fase)
      if (i > 0 && !printedFase) parts.push(makeEmptyPara())

      // Encabezado del criterio en negrita (label opcional para variantes "Criterio 1 (Columna 1)")
      const encabezado = sub.label || `Criterio ${sub.numero}`
      parts.push(makePara(
        `${encabezado} — ¿Se cumple? ${estado}`,
        true,
        { justify: false, spaceBefore: 120, spaceAfter: 60 }
      ))

      // Descripción del criterio en negrita cursiva
      parts.push(makePara(texCriterio, true, { italic: true, spaceAfter: 100 }))

      // Justificación — texto justificado
      if (justif) {
        justif.split('\n').filter(l => l.trim()).forEach(line => {
          parts.push(makePara(line.trim(), false, { spaceAfter: 80 }))
        })
      }
    }

    // Tabla comparativa · desactivada intencionadamente en el informe final por decisión de la asesoría
    // (los datos comparativos se explican en el texto narrativo del propio criterio).
    // Los datos siguen preservándose en `resultado.tabla_comparativa` por si otros módulos los leen.

    if (resultado.resumen) {
      parts.push(makeEmptyPara())
      parts.push(makePara('Resumen:', true, { justify: false, spaceAfter: 80 }))
      parts.push(makePara(resultado.resumen, false, { spaceAfter: 80 }))
    }

  } else if (resultado && typeof resultado === 'object') {
    // Formato alternativo (ej. MAT 3): sin subcriteria, pero con resumen_ejecutivo, nivel_cumplimiento, riesgos
    parts.push(makePara(`Puntuación obtenida: ${ptsLabel}`, true, { justify: false, spaceAfter: hasAudit ? 80 : 160 }))
    if (hasAudit) parts.push(makeAuditBlock())

    // Resumen ejecutivo arriba (estilo ABAMA)
    if (resultado.resumen_ejecutivo) {
      parts.push(makePara('Resumen:', true, { justify: false, spaceBefore: 80, spaceAfter: 80 }))
      resultado.resumen_ejecutivo.split('\n').filter(l => l.trim()).forEach(line => {
        parts.push(makePara(line.trim(), false, { spaceAfter: 80 }))
      })
    }

    if (resultado.nivel_cumplimiento_alcanzado) {
      parts.push(makeEmptyPara())
      parts.push(makePara('Nivel de cumplimiento alcanzado:', true, { justify: false, spaceBefore: 120, spaceAfter: 60 }))
      parts.push(makePara(resultado.nivel_cumplimiento_alcanzado, false, { spaceAfter: 100 }))
    }

    if (Array.isArray(resultado.evidencias_verificadas) && resultado.evidencias_verificadas.length > 0) {
      parts.push(makeEmptyPara())
      parts.push(makePara('Evidencias verificadas:', true, { justify: false, spaceBefore: 160, spaceAfter: 80 }))
      resultado.evidencias_verificadas.forEach(ev => {
        const line = typeof ev === 'string' ? ev : (ev.descripcion || ev.nombre || JSON.stringify(ev))
        parts.push(makePara(`• ${line}`, false, { spaceAfter: 60 }))
      })
    }

    if (Array.isArray(resultado.riesgos_auditor) && resultado.riesgos_auditor.length > 0) {
      parts.push(makeEmptyPara())
      parts.push(makePara('Riesgos identificados por el asesor:', true, { justify: false, spaceBefore: 160, spaceAfter: 80 }))
      resultado.riesgos_auditor.forEach(r => {
        const line = typeof r === 'string' ? r : (r.descripcion || JSON.stringify(r))
        parts.push(makePara(`• ${line}`, false, { spaceAfter: 60 }))
      })
    }

    if (Array.isArray(resultado.evidencias_faltantes) && resultado.evidencias_faltantes.length > 0) {
      parts.push(makeEmptyPara())
      parts.push(makePara('Evidencias pendientes de aportar:', true, { justify: false, spaceBefore: 160, spaceAfter: 80 }))
      resultado.evidencias_faltantes.forEach(ev => {
        const line = typeof ev === 'string' ? ev : (ev.descripcion || ev.nombre || JSON.stringify(ev))
        parts.push(makePara(`• ${line}`, false, { spaceAfter: 60 }))
      })
    }
  } else if (ev.justificacion) {
    parts.push(makePara(`Puntuación obtenida: ${ptsLabel}`, true, { justify: false, spaceAfter: 160 }))
    const rawText = typeof ev.justificacion === 'string'
      ? ev.justificacion
      : JSON.stringify(ev.justificacion, null, 2)
    rawText.split('\n').filter(l => l.trim()).forEach(l => parts.push(makePara(l.trim(), false, { spaceAfter: 80 })))
  } else {
    parts.push(makePara('Justificación pendiente de completar por el Asesor BREEAM ES.', false, { spaceAfter: 80 }))
  }

  return parts.join('')
}

// ============================================================
// HELPER — Tabla comparativa de valores BREEAM
// ============================================================

function makeTableCell(text, bold = false, width = 2340, shading = null) {
  const t = esc(text)
  const shade = shading ? `<w:shd w:val="clear" w:color="auto" w:fill="${shading}"/>` : ''
  const run = bold
    ? `<w:r><w:rPr><w:b/><w:sz w:val="18"/><w:szCs w:val="18"/></w:rPr><w:t xml:space="preserve">${t}</w:t></w:r>`
    : `<w:r><w:rPr><w:sz w:val="18"/><w:szCs w:val="18"/></w:rPr><w:t xml:space="preserve">${t}</w:t></w:r>`
  return `<w:tc>
    <w:tcPr><w:tcW w:w="${width}" w:type="dxa"/>${shade}
      <w:tcBorders>
        <w:top w:val="single" w:sz="4" w:space="0" w:color="9A8A7A"/>
        <w:left w:val="single" w:sz="4" w:space="0" w:color="9A8A7A"/>
        <w:bottom w:val="single" w:sz="4" w:space="0" w:color="9A8A7A"/>
        <w:right w:val="single" w:sz="4" w:space="0" w:color="9A8A7A"/>
      </w:tcBorders>
      <w:tcMar><w:top w:w="60" w:type="dxa"/><w:left w:w="100" w:type="dxa"/><w:bottom w:w="60" w:type="dxa"/><w:right w:w="100" w:type="dxa"/></w:tcMar>
    </w:tcPr>
    <w:p><w:pPr><w:rPr>${bold ? '<w:b/>' : ''}<w:sz w:val="18"/></w:rPr></w:pPr>${run}</w:p>
  </w:tc>`
}

// eslint-disable-next-line no-unused-vars
function makeComparisonTable(rows) {
  // Anchos de columna (total ~9360 dxa para A4 con márgenes estándar)
  const colWidths = [2300, 2300, 3160, 1600]

  const tblGrid = colWidths.map(w => `<w:gridCol w:w="${w}"/>`).join('')

  const tblBorders = `<w:tblBorders>
    <w:top w:val="single" w:sz="4" w:space="0" w:color="3B2F2F"/>
    <w:left w:val="single" w:sz="4" w:space="0" w:color="3B2F2F"/>
    <w:bottom w:val="single" w:sz="4" w:space="0" w:color="3B2F2F"/>
    <w:right w:val="single" w:sz="4" w:space="0" w:color="3B2F2F"/>
    <w:insideH w:val="single" w:sz="4" w:space="0" w:color="9A8A7A"/>
    <w:insideV w:val="single" w:sz="4" w:space="0" w:color="9A8A7A"/>
  </w:tblBorders>`

  // Fila de encabezado
  const headerRow = `<w:tr>
    ${makeTableCell('Parámetro', true, colWidths[0], 'F0EBE4')}
    ${makeTableCell('Exigencia BREEAM', true, colWidths[1], 'F0EBE4')}
    ${makeTableCell('Valor obtenido', true, colWidths[2], 'F0EBE4')}
    ${makeTableCell('¿Cumple?', true, colWidths[3], 'F0EBE4')}
  </w:tr>`

  // Filas de datos
  const dataRows = rows.map((row, i) => {
    const bg = i % 2 === 0 ? null : 'FAF7F3'
    const cumpleText = row.cumple === true ? 'SÍ' : row.cumple === false ? 'NO' : 'PARCIAL'
    return `<w:tr>
      ${makeTableCell(row.parametro || '', false, colWidths[0], bg)}
      ${makeTableCell(row.valor_requerido || '', false, colWidths[1], bg)}
      ${makeTableCell(row.valor_obtenido || '', false, colWidths[2], bg)}
      ${makeTableCell(cumpleText, true, colWidths[3], row.cumple === true ? 'E8F5D0' : row.cumple === false ? 'FDE8E4' : 'FFF5E0')}
    </w:tr>`
  }).join('')

  return `<w:tbl>
    <w:tblPr>
      <w:tblW w:w="9360" w:type="dxa"/>
      ${tblBorders}
      <w:tblLook w:val="04A0" w:firstRow="1" w:lastRow="0" w:firstColumn="1" w:lastColumn="0" w:noHBand="0" w:noVBand="1"/>
    </w:tblPr>
    <w:tblGrid>${tblGrid}</w:tblGrid>
    ${headerRow}
    ${dataRows}
  </w:tbl>`
}

// ============================================================
// FILL PROJECT FIELDS · Portada + datos del edificio
// ============================================================

/**
 * Sustituye los placeholders rojos ([Nombre del edificio evaluado], descripciones, etc.)
 * en la portada y sección "Datos del Edificio" con los datos del proyecto.
 * Los datos vienen de PROJECT-DETAILS (guardado como evaluation con criteria_code='PROJECT-DETAILS').
 */
function fillProjectFields(xml, project) {
  const evaluations = Object.values(project.evaluations || {})
  const pdEval = evaluations.find(ev => ev.criteria_code === 'PROJECT-DETAILS')
  if (!pdEval || !pdEval.justificacion) return xml

  let pd = null
  try {
    pd = typeof pdEval.justificacion === 'string'
      ? JSON.parse(pdEval.justificacion)
      : pdEval.justificacion
  } catch (_) { return xml }
  if (!pd) return xml

  const today = pd.fechaInforme || new Date().toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })
  const nombreEdificio = pd.nombreEdificio || project.name || ''
  const asesor = pd.asesor || ''
  const organizacion = pd.organizacion || ''
  const contacto = pd.contactoAsesor || ''
  const ref = pd.codigoBreeam || ''
  const version = pd.version || 'SD5075 Issue 1.0'
  const fase = (pd.faseEvaluacion || '').toLowerCase().includes('post') ? 'Final' : 'Provisional'
  const cliente = pd.cliente || ''

  // Puntuación ponderada (para SÍ/NO de la tabla Requisitos Mínimos por clasificación)
  const pct = project.breeamPct != null
    ? Math.round(project.breeamPct)
    : Math.round(((project.totalPoints || 0) / (project.maxPoints || 110)) * 100)

  // Compromiso final del cliente/asesor
  const compromiso = `${organizacion} y ${cliente} nos comprometemos a proporcionar la información relevante del edificio/proyecto y dar a BREEAM ES el permiso para publicar un Caso de Estudio sobre el diseño y construcción de ${nombreEdificio}.`

  // Mapa de sustituciones: texto exacto placeholder → texto real
  // Los textos son los que aparecen en color rojo en la plantilla oficial
  const subs = [
    // ── Portada ──────────────────────────────────
    ['[Nombre del edificio evaluado]', nombreEdificio],
    ['[Nombre del Edificio Evaluado]', nombreEdificio],
    ['[nombre del edificio]', nombreEdificio],
    ['[Asesor]', asesor],
    ['[Día Mes Año]', today],
    ['[referencia BREEAM ES]', ref],
    ['[Nombre de la Organización Autorizada]', organizacion],
    ['[Datos de contacto del Asesor]', contacto],
    ['[Organización Autorizada]', organizacion],
    ['[organización autorizada/cliente]', `${organizacion} · ${cliente}`],
    ['[Cliente]', cliente],
    ['[Cliente e]', cliente],
    ['[Esquema]', pd.esquema || 'Nueva Construcción'],
    ['[Versión]', version],
    ['[versió n]', version],
    ['[versión]', version],
    ['Informe Provisional /Final de Evaluación', `Informe ${fase} de Evaluación`],
    ['Fase de Diseño – Fase de Post-Construcción (borre según corresponda)', fase === 'Final' ? 'Fase de Post-Construcción' : 'Fase de Diseño'],

    // ── Datos del edificio ──────────────────────
    ['Introduzca una descripción del proyecto y el edificio, p.ej. para qué se usará el edificio y qué alojamiento proporciona', pd.descripcionProyecto || ''],
    ['Introduzca una descripción del emplazamiento, su situación y paisaje, p.ej. es una reforma, un terreno industrial o virgen, etc.', pd.descripcionEmplazamiento || ''],
    ['Introduzca la huella bruta del edificio, la superficie útil y construida del edificio y la superficie del emplazamiento.', pd.descripcionSuperficies || ''],
    ['Descripción de las especificaciones de las fachadas nuevas y reutilizados (si es aplicable)', pd.descripcionFachadas || ''],
    ['Descripción de las especificaciones de las cubiertas nuevas y reutilizadas (si es aplicable)', pd.descripcionCubiertas || ''],
    ['Descripción de las especificaciones del forjado de suelo y de pisos superiores nuevos y reutilizados (si es aplicable)', pd.descripcionForjados || ''],
    ['Descripción del sistema o estrategia de suministro de calefacción al edificio', pd.descripcionCalefaccion || ''],
    ['Descripción del sistema o estrategia de ventilación del edificio', pd.descripcionVentilacion || ''],
    ['Descripción del sistema o estrategia de refrigeración del edificio', pd.descripcionRefrigeracion || ''],
    ['Descripción del sistema o estrategia de suministro de agua caliente al edificio', pd.descripcionACS || ''],
    ['Añada como corresponda, p. ej. sistemas de energías renovables, otras innovaciones, etc.', pd.descripcionInnovaciones || ''],

    // ── Compromiso final del cliente + Innovaciones ──────────────
    ['Yo/nosotros [organización autorizada/cliente] me/nos comprometo/comprometemos a proporcionar/adjuntar (elimine cuando sea necesario) la información relevante del edificio/proyecto y dar a BREEAM ES el permiso para publicar un Caso de Estudio sobre el diseño y construcción de [nombre del edificio].', compromiso],
    ['Si no hay Innovaciones Aprobadas en este proyecto, borre la tabla de abajo e indique “No se han presentado Innovaciones para este proyecto” en la sección de documentación a entregar y la siguiente sección de justificación.', 'No se han presentado Innovaciones Aprobadas por BREEAM ES para este proyecto. Los puntos INO 1 obtenidos provienen del cumplimiento de criterios de Nivel Ejemplar en Requisitos existentes (según NA1 del criterio INO 1 del Manual BREEAM ES NC 2015).'],
  ]

  // Cada sustitución busca el texto dentro de un <w:t>...</w:t> y lo reemplaza
  // Para eliminar el color rojo, buscamos el <w:p> completo que lo contiene y
  // sustituimos su contenido con un nuevo párrafo en color negro/normal.
  for (const [placeholder, replacement] of subs) {
    if (!replacement) continue
    xml = replaceRedPlaceholder(xml, placeholder, replacement)
  }

  // Sustituir 5 SÍ/NO de la tabla "Requisitos Mínimos conseguidos" por clasificación
  // BREEAM ES: Correcto ≥30% · Bueno ≥45% · Muy Bueno ≥55% · Excelente ≥70% · Excepcional ≥85%
  const cumple = [pct >= 30, pct >= 45, pct >= 55, pct >= 70, pct >= 85]
  xml = replaceSiNoTable(xml, cumple)

  // Rellenar celdas vacías del Formulario de Solicitud de Certificado (pág. 8)
  xml = fillSolicitudForm(xml, pd, pct)

  return xml
}

/**
 * Rellena las celdas vacías del Formulario de Solicitud de Certificado (pág. 8 plantilla)
 * con datos del proyecto (código de registro, fase, fecha, superficie, etc.).
 */
function fillSolicitudForm(xml, pd, pct) {
  const fase = (pd.faseEvaluacion || '').toLowerCase().includes('post') ? 'Post-Construcción (final)' : 'Diseño (provisional)'
  const nombreDireccion = `${pd.nombreEdificio || ''}\n${pd.direccion || ''}${pd.codigoPostal ? ' · CP ' + pd.codigoPostal : ''}${pd.municipality ? ' · ' + pd.municipality : ''}${pd.province ? ' (' + pd.province + ')' : ''}`
  const superficie = pd.superficieConstruida ? `${pd.superficieConstruida} m²` : ''
  const esquemaVersion = `${pd.esquema || 'Nueva Construcción'} · ${pd.version || 'SD5075 Issue 1.0'}`

  // Mapa: label → valor a insertar en celda derecha
  const formSubs = [
    ['Código de Registro', pd.codigoBreeam || ''],
    ['Fecha del Informe', pd.fechaInforme || ''],
    ['Superficie Construida evaluada', superficie],
    ['% puntuación', pct != null ? `${pct} %` : ''],
    ['Esquema y Versión Manual BREEAM ES', esquemaVersion],
    ['Nombre del edificio/', nombreDireccion],
    ['Asesor Licenciado', pd.asesor || ''],
    ['Organización Autorizada', pd.organizacion || ''],
    ['Cliente', pd.cliente || ''],
    ['Promotor', pd.promotor || ''],
    ['Arquitecto', pd.arquitecto || ''],
    ['Constructor', pd.constructor || ''],
  ]

  for (const [label, value] of formSubs) {
    if (!value) continue
    xml = fillTableCellAfterLabel(xml, label, value)
  }

  // Sustituir el texto "Fase de Diseño – Fase de Post-Construcción (borre según corresponda)"
  // dentro del Formulario (celda de la fila "Fase de evaluación")
  xml = replaceInsideCell(xml, 'Fase de Diseño – Fase de Post-Construcción (borre según corresponda)', fase)
  xml = replaceInsideCell(xml, 'Fase de Diseño – Fase de Post-Construcción (borre según corresponda )', fase)

  return xml
}

/**
 * Busca una fila de tabla <w:tr> que contenga el label como texto en su primera celda.
 * En la SEGUNDA celda de esa fila (asumida vacía), inserta un párrafo con el valor.
 * Si la fila tiene 4 celdas y el label está en la celda 2 (índice 2), rellena la celda 3.
 */
function fillTableCellAfterLabel(xml, label, value) {
  const trRe = /<w:tr\b[^>]*>[\s\S]*?<\/w:tr>/g
  let result = ''
  let lastEnd = 0
  let m
  while ((m = trRe.exec(xml)) !== null) {
    const tr = m[0]
    if (!tr.includes(label)) continue
    // Extraer celdas <w:tc>...</w:tc>
    const cellRe = /<w:tc\b[^>]*>[\s\S]*?<\/w:tc>/g
    const cells = []
    let cm
    while ((cm = cellRe.exec(tr)) !== null) {
      cells.push({ start: cm.index, end: cm.index + cm[0].length, text: cm[0] })
    }
    // Encontrar la celda que contiene el label
    let labelCellIdx = -1
    for (let i = 0; i < cells.length; i++) {
      const cellText = cells[i].text.replace(/<[^>]+>/g, '').trim()
      if (cellText.includes(label.trim())) { labelCellIdx = i; break }
    }
    if (labelCellIdx < 0 || labelCellIdx + 1 >= cells.length) continue
    // Rellenar la celda siguiente
    const targetCell = cells[labelCellIdx + 1]
    const targetText = targetCell.text.replace(/<[^>]+>/g, '').trim()
    // Solo rellenar si la celda está vacía (o casi)
    if (targetText.length > 3) continue
    // Construir nueva celda preservando el <w:tcPr> original
    const tcPrMatch = targetCell.text.match(/<w:tcPr\b[\s\S]*?<\/w:tcPr>/)
    const tcPr = tcPrMatch ? tcPrMatch[0] : ''
    // Convertir saltos de línea del value en <w:br/>
    const lines = value.split('\n')
    const runsXml = lines.map((line, i) => {
      const brXml = i > 0 ? '<w:br/>' : ''
      return `<w:r>${brXml}<w:t xml:space="preserve">${esc(line)}</w:t></w:r>`
    }).join('')
    const newCell = `<w:tc>${tcPr}<w:p><w:pPr><w:spacing w:before="40" w:after="40"/></w:pPr>${runsXml}</w:p></w:tc>`
    // Reconstruir el tr con la nueva celda
    const newTr = tr.slice(0, targetCell.start) + newCell + tr.slice(targetCell.end)
    // Reemplazar el tr en el xml completo
    result += xml.slice(lastEnd, m.index) + newTr
    lastEnd = m.index + tr.length
  }
  result += xml.slice(lastEnd)
  return result || xml
}

/**
 * Sustituye un texto dentro de una celda de tabla (no un párrafo suelto).
 * Útil para el placeholder "Fase de Diseño – Fase de Post-Construcción (borre según corresponda)"
 * que está dentro de una celda del Formulario.
 */
function replaceInsideCell(xml, target, replacement) {
  const tcRe = /<w:tc\b[^>]*>[\s\S]*?<\/w:tc>/g
  let result = ''
  let lastEnd = 0
  let m
  while ((m = tcRe.exec(xml)) !== null) {
    const tc = m[0]
    const text = tc.replace(/<[^>]+>/g, '').trim()
    if (!text.includes(target.trim())) continue
    // Preservar <w:tcPr>
    const tcPrMatch = tc.match(/<w:tcPr\b[\s\S]*?<\/w:tcPr>/)
    const tcPr = tcPrMatch ? tcPrMatch[0] : ''
    const newCell = `<w:tc>${tcPr}<w:p><w:pPr><w:spacing w:before="40" w:after="40"/></w:pPr><w:r><w:t xml:space="preserve">${esc(replacement)}</w:t></w:r></w:p></w:tc>`
    result += xml.slice(lastEnd, m.index) + newCell
    lastEnd = m.index + tc.length
  }
  result += xml.slice(lastEnd)
  return result || xml
}

/**
 * Sustituye los 5 SÍ/NO de la tabla "Requisitos Mínimos conseguidos" por clasificación
 * en el orden Correcto · Bueno · Muy Bueno · Excelente · Excepcional.
 */
function replaceSiNoTable(xml, cumple) {
  // Buscar cada aparición del texto "SÍ/NO" en párrafos rojos y sustituir uno a uno
  const paraRe = /<w:p\b[^>]*>[\s\S]*?<\/w:p>/g
  let result = ''
  let lastEnd = 0
  let m
  let idx = 0
  while ((m = paraRe.exec(xml)) !== null) {
    const para = m[0]
    const textOnly = para.replace(/<[^>]+>/g, '').trim()
    if (textOnly !== 'SÍ/NO' && !(textOnly.endsWith('SÍ/NO') && textOnly.length < 15)) continue
    const isRed = para.includes('FF0000') || para.includes('C00000') || para.includes('ff0000')
    if (!isRed) continue
    if (idx >= cumple.length) break
    const replacement = cumple[idx] ? 'SÍ' : 'NO'
    const newPara = `<w:p><w:pPr><w:spacing w:before="0" w:after="0"/></w:pPr><w:r><w:rPr><w:b/></w:rPr><w:t xml:space="preserve">${replacement}</w:t></w:r></w:p>`
    result += xml.slice(lastEnd, m.index) + newPara
    lastEnd = m.index + para.length
    idx++
  }
  result += xml.slice(lastEnd)
  return result || xml
}

/**
 * Busca un texto placeholder dentro de párrafos con color rojo y sustituye
 * el párrafo entero por uno nuevo con el texto de reemplazo en color negro.
 * Si el texto no está en un párrafo rojo, hace una sustitución simple de texto.
 */
function replaceRedPlaceholder(xml, placeholder, replacement) {
  // Buscar todos los <w:p>...</w:p> que contengan el placeholder
  const paraRe = /<w:p\b[^>]*>[\s\S]*?<\/w:p>/g
  let result = ''
  let lastEnd = 0
  let m
  while ((m = paraRe.exec(xml)) !== null) {
    const para = m[0]
    // Extraer texto plano del párrafo
    const textOnly = para.replace(/<[^>]+>/g, '').trim()
    if (!textOnly.includes(placeholder.trim())) continue
    // Verificar si es rojo
    const isRed = para.includes('FF0000') || para.includes('C00000') || para.includes('ff0000')
    if (!isRed) continue
    // Reemplazar el párrafo entero con uno nuevo en color negro
    const newPara = `<w:p><w:pPr><w:spacing w:before="80" w:after="80"/><w:jc w:val="both"/></w:pPr><w:r><w:t xml:space="preserve">${esc(replacement)}</w:t></w:r></w:p>`
    result += xml.slice(lastEnd, m.index) + newPara
    lastEnd = m.index + para.length
  }
  result += xml.slice(lastEnd)
  return result || xml
}


// ============================================================
// INO 1 · Rellenado especial bajo «Innovaciones Aprobadas»
// ============================================================
// La plantilla oficial solo trae el título "Innovaciones Aprobadas" (Ttulo20) sin los
// subtítulos "Objetivo/Documentación/Justificación" de los criterios estándar. Insertamos
// el bloque de INO 1 (puntuación + resumen + subcriterios) directamente tras el heading,
// preservando el <w:sectPr> final si existe.
function fillInnovacionesSection(section, ev, BREEAM_CRITERIA) {
  // 1) Localizar el final del párrafo del heading (el primer </w:p> desde el inicio).
  const headingParaEnd = section.indexOf('</w:p>', 0) + '</w:p>'.length
  if (headingParaEnd <= 6) return section

  // 2) Localizar el ÚLTIMO <w:sectPr> de la sección. En la plantilla este sectPr puede
  //    aparecer como elemento suelto del body (no necesariamente dentro de un <w:p>).
  //    Buscamos hacia atrás desde </w:body> (o desde el final) para dar con el que
  //    define el cierre del cuerpo del documento.
  const bodyCloseIdx = section.indexOf('</w:body>')
  const searchLimit = bodyCloseIdx !== -1 ? bodyCloseIdx : section.length
  const sectStart = section.lastIndexOf('<w:sectPr', searchLimit)

  // 3) Construir el bloque INO 1 reutilizando la lógica estándar de justificación.
  const inoBlock = buildJustificationXml(ev, BREEAM_CRITERIA)

  // 4) Preservar TODO desde el <w:sectPr> hasta el final de la sección (incluye el
  //    </w:sectPr>, </w:body> y </w:document> si están presentes). Si no hay sectPr,
  //    conserva el resto de la sección tal cual.
  const preservedTail = sectStart !== -1
    ? section.slice(sectStart)
    : section.slice(headingParaEnd)

  // 5) Reemplazar el contenido entre el heading y el sectPr por el bloque INO.
  return section.slice(0, headingParaEnd) + inoBlock + preservedTail
}
